import _ from 'lodash';
import async from '@/scripts/tools/async';
import { path as pathLib } from '@/scripts/tools/utils';
import cos from '@/scripts/helper/cos';

/**
 * 判读 acl 是否
 * @param {String} acl
 */
export function isPublic(acl) {
  return ['public-read', 'public-read-write'].includes(acl);
}

export function renameFolder() {}

/**
    文件的 meta 属性信息，通过 headObject 获取
    用于传参给 sliceCopyFile 和 参与文件一致性校验

    注意： ETag 不参与一致性校验，因为 sliceCopyFile 成功的文件，ETag 可能改变
  */
const MetaHeaderMap = {
  // 这部分属性需要传递给 sliceCopyFile
  'x-cos-storage-class': 'StorageClass',
  'cache-control': 'CacheControl',
  'content-disposition': 'ContentDisposition',
  'content-encoding': 'ContentEncoding',
  'content-type': 'ContentType',
  expect: 'Expect',
  expires: 'Expires',
  'x-cos-server-side-encryption-customer-algorithm': 'SSECustomerAlgorithm',
  'x-cos-server-side-encryption-customer-key': 'SSECustomerKey',
  'x-cos-server-side-encryption-customer-key-MD5': 'SSECustomerKeyMD5',
  'x-cos-server-side-encryption': 'ServerSideEncryption',
  'x-cos-server-side-encryption-cos-kms-key-id': 'SSEKMSKeyId',
  'x-cos-server-side-encryption-context': 'SSEContext',
  'x-cos-meta-*': 'x-cos-meta-*',
  'x-cos-restore': 'x-cos-restore',

  // 这部分属性不传递给 sliceCopyFile，仅参与文件一致性校验
  // 'content-length': 'ContentLength',
  etag: 'ETag',
};

/**
  获取文件的 meta 信息，返回的字段见 MetaHeaderMap
  涉及 x-cos-meta-* 的头部，保留原样返回
  {
    Bucket,
    Region,
    Key,
  }
  callback
  {
    StorageClass,
    CacheControl,
    ContentDisposition,
    ...
  }
*/
export function getObjectMeta(params, callback) {
  cos.headObject(params, (err, data) => {
    if (err) {
      if (err.error === 'Forbidden' && err.statusCode === 403) {
        err = {
          error: {
            Code: 'AccessDenied',
            statusCode: 403,
          },
        };
      }
      err.errorAction = 'headObject';
      callback(err);
      return;
    }

    const meta = Object.keys(data.headers || {}).reduce((res, key) => {
      if (key.indexOf('x-cos-meta-') === 0) {
        res[key] = data.headers[key];
      } else if (MetaHeaderMap[key]) {
        res[MetaHeaderMap[key]] = data.headers[key];
      }
      return res;
    }, {});

    callback(null, meta);
  });
}

export function getObjectMetaAndAcl(params, cb) {
  return new Promise((resolve, reject) => {
    const callback = (err, data) => {
      if (cb) {
        cb(err, data);
      } else {
        err ? reject(err) : resolve(data);
      }
    };
    const { disabled } = params;
    if (disabled && disabled.includes('acl')) {
      getObjectMeta(params, (err, data) => {
        if (err) {
          callback(err);
          return;
        }

        callback(null, data);
      });
    } else {
      cos.getObjectAcl(params, (err, data) => {
        if (err) {
          err.errorAction = 'getObjectAcl';
          callback(err);
          return;
        }

        if (/^tce-.*/.test(data.ACL)) {
          data.ACL = data.ACL.replace(/^tce-/, '');
        }

        const result = {
          ACL: data.ACL,
          GrantRead: data.GrantRead,
          GrantReadAcp: data.GrantReadAcp,
          GrantWrite: data.GrantWrite,
          GrantWriteAcp: data.GrantWriteAcp,
          GrantFullControl: data.GrantFullControl,
        };

        getObjectMeta(params, (_err, _data) => {
          if (_err) {
            callback(_err);
            return;
          }

          callback(null, { ...result, ..._data });
        });
      });
    }
  });
}

/**
 * 检测是否有 getObjectAcl 权限
 * @param {Object} params
 */
export function checkIfHasGetObjectAcl(params) {
  return new Promise(resolve => {
    cos.getObjectAcl(params, err => {
      let hasPermission = true;
      if (err) {
        const { error } = err;
        if (error && error.Code === 'AccessDenied') {
          hasPermission = false;
        }
      }
      const disabled = hasPermission ? '' : 'acl';
      resolve(disabled);
    });
  });
}

// 需要进行分块的限制，文件大小高于这个值，则进行分块 copy，同地域没有大小限制
const MB = 1024 * 1024;
const GB = 1024 * 1024 * 1024;
const SliceSizeMap = {
  sameRegion: 5 * GB, // 如果是同地域，且涉及存储类型修改，则分块限制为 5GB
  crossRegion: 10 * MB, // 如果是非同地域，则分块限制为 10MB
};

/**
  对 单个文件 进行分片 Copy
  {
    source: {
      Bucket,
      Region,
      Prefix,
      Key
    },

    target: {
      Bucket,
      Region,
      Prefix,
      Key,      // 非必须
      Headers   // 非必须
    },

    metaAndAcl: {
      StorageClass,
      ContentLength
    },           // 非必须

    onProgress(info) {

    }
  }
*/
export function sliceCopyFile(options, callback) {
  const {
    source, target, onProgress, disabled,
  } = options;

  getObjectMetaAndAcl(
    {
      Bucket: source.Bucket,
      Region: source.Region,
      Key: source.Key,
      disabled,
    },
    (err, metaAndAcl) => {
      if (err) {
        callback(err);
        return;
      }

      const originMetaAndAcl = metaAndAcl; // 源文件的头部信息
      // 如果参数中传递了 metaAndAcl，说明希望修改头部信息
      if (options.metaAndAcl && metaAndAcl) {
        // 本次需要设置的头部信息
        metaAndAcl = { ...originMetaAndAcl, ...options.metaAndAcl, MetadataDirective: 'Replaced' };
      }

      // 如果是同地域，则不限制分块大小
      const ContentLength = metaAndAcl.ContentLength * 1;
      let CopySliceSize = source.Region === target.Region ? ContentLength + 1 : SliceSizeMap.crossRegion;

      /**
       * 如果本次设置的存储类型，和源文件的不同，且文件大于 5GB
       * 不能直接用 putObjectCopy 复制整个文件，需要分块 Copy
       */
      if (
        metaAndAcl.StorageClass !== originMetaAndAcl.StorageClass
        && ContentLength > SliceSizeMap.sameRegion
      ) {
        CopySliceSize = SliceSizeMap.sameRegion;
      }

      const params = {
        Bucket: target.Bucket,
        Region: target.Region,
        Key: target.Key,
        CopySource: source.Location,
        Headers: target.Headers,
        CopySliceSize,
        onProgress,
      };

      Object.assign(params, metaAndAcl, { ETag: null, ContentLength: null });

      /**
       * 对归档存储文件进行特判
       */
      if (originMetaAndAcl.StorageClass === 'ARCHIVE' || originMetaAndAcl.StorageClass === 'DEEP_ARCHIVE') {
        err = {
          error: {
            Code: 'InvalidObjectState',
            Message: 'Archive object is not allowed to be copied',
          },
        };

        if (originMetaAndAcl['x-cos-restore']) {
          // 正在回热中，则 Copy 一定失败，直接报错
          if (originMetaAndAcl['x-cos-restore'].indexOf('ongoing-request="true"') > -1) {
            err = {
              error: {
                Code: 'RestoreAlreadyInProgress',
              },
            };
          } else if (originMetaAndAcl['x-cos-restore'].indexOf('expiry-date') > -1) {
            err = null;
          }
        }

        if (err) {
          err.errorAction = 'sliceCopyFile';
          callback(err);
          return;
        }
      }
      const copyCb = _err => {
        // 如果存储类型是归档存储，且获取不到 Content-Length，则视为未恢复
        if ((originMetaAndAcl.StorageClass === 'ARCHIVE' || originMetaAndAcl.StorageClass === 'DEEP_ARCHIVE')
            && err
            && err.error
            && _.isString(err.error)
            && err.error.indexOf('get Content-Length error') > -1) {
          err = {
            error: {
              Code: 'InvalidObjectState',
              Message: 'Archive object is not allowed to be copied',
            },
          };
        }

        if (_err) {
          _err.errorAction = 'sliceCopyFile';
        }
        callback(_err, {
          source,
          target,
          metaAndAcl,
        });
      };
      if (source.Region === target.Region || source.Size === 0) {
        cos.putObjectCopy(params, copyCb);
      } else {
        cos.sliceCopyFile(params, copyCb);
      }
    },
  );
}

export async function checkSameMetaAndAclList(list, callback) {
  const result = { sameList: [], notSameList: [] };
  if (list.length === 0) {
    callback(null, result);
    return;
  }

  const checkFile = list[0];
  const disabled = await checkIfHasGetObjectAcl(checkFile.source);

  async.eachLimit(
    list,
    1,
    (file, next) => {
      const { metaAndAcl, source, target } = file; // 源文件的 meta 和 ACL | 目标文件
      delete target.Headers;
      getObjectMetaAndAcl({ ...target, disabled }, (err, targetMetaAndAcl) => {
        if (err) {
          file.error = err;
          result.notSameList.push(file);
          next(err);
          return;
        }
        if (pathLib.extname(source.Key) !== pathLib.extname(target.Key)) {
          delete metaAndAcl.ContentType;
          delete targetMetaAndAcl.ContentType;
        }
        delete metaAndAcl.ETag;
        delete targetMetaAndAcl.ETag;

        const isSame = _.isEqual(metaAndAcl, targetMetaAndAcl);
        if (isSame) {
          result.sameList.push(file);
        } else {
          result.notSameList.push(file);
        }
        next();
      });
    },
    () => {
      callback(null, result);
    },
  );
}

/**
   检测多个文件是否拥有相同的 ACL 信息 和 Meta 信息
    [
      {
        Bucket,
        Region,
        Key,
      },
      ...
    ]
    callback
    {
      ...getObjectMetaAndAcl 返回的信息（不包括 ETag 信息）
    }
  */
export function checkObjectsSame(objects, callback) {
  let currentSameData;
  let currentObject;
  let errorMsg;
  let isSame = true;

  let ignoreContentType = false;
  let ext;

  // 如果存在不同后缀名的文件，则忽略 Content-Type 一致性校验
  objects.forEach(object => {
    if (ext === undefined) {
      ext = pathLib.extname(object.Key);
    } else if (pathLib.extname(object.Key) !== ext) {
      ignoreContentType = true;
    }
  });

  async.eachLimit(
    objects,
    1,
    (object, next) => {
      getObjectMetaAndAcl(object, (err, sameData) => {
        if (err) {
          errorMsg = err;
          next(err);
          return;
        }

        delete sameData.ETag;

        if (ignoreContentType) {
          delete sameData.ContentType;
        }

        if (!currentSameData || !currentObject) {
          currentObject = object;
          currentSameData = sameData;
        } else {
          isSame = isSame && _.isEqual(currentSameData, sameData);
        }

        if (!isSame) {
          errorMsg = {
            error: 'checkObjectsSame',
            current: { object: currentObject, ...currentSameData },
            different: { object, ...sameData },
          };
        }

        next(!isSame);
      });
    },
    () => {
      if (errorMsg) {
        callback(errorMsg);
        return;
      }

      callback(null, currentSameData);
    },
  );
}
